import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlarmClock, Coffee, LogIn, LogOut, UtensilsCrossed } from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { APP_PATHS } from '@/constant/app-paths';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import {
  ClockStatusBadge,
  formatElapsed,
  useAttendanceClock,
} from '@/modules/time-attendance/attendance/hooks/use-attendance-clock';
import { formatTimeOfDay } from '@/modules/time-attendance/attendance/services/attendance-rules';
import { useAuthStore } from '@/state/store/auth';

/** "Good morning / afternoon / evening" by the hour in the organization's time zone. */
const greetingKey = (now = new Date()) => {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      hour12: false,
      timeZone: getOrganizationTimeZone(),
    }).format(now)
  );
  if (hour < 12) return 'HOME_GOOD_MORNING';
  if (hour < 17) return 'HOME_GOOD_AFTERNOON';
  return 'HOME_GOOD_EVENING';
};

/** A clock-in/out time as "HH:mm" on the clock of the zone the day was recorded in, "--:--" without one. */
const timeOf = (iso?: string | null, timeZone?: string) =>
  iso && !Number.isNaN(new Date(iso).getTime()) ? formatTimeOfDay(iso, false, timeZone) : '--:--';

/** One of the day's facts (prototype `.home-fact`): a 12px muted label over a 16px tabular value. */
const Fact = ({ label, value }: { label: string; value: string }) => (
  <span className="grid gap-0.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <b className="text-base font-semibold tabular-nums">{value}</b>
  </span>
);

/**
 * The timer row of the band (prototype `homeHero` › `.home-clock`): status and worked time left; on the
 * right the day's facts (Clock in, Break when breaks apply, Clock out), then the actions and the first
 * rule message. Everything stacks on a phone.
 */
const HeroClock = () => {
  const { t } = useTranslation();
  const clock = useAttendanceClock();
  const { phase, show, busy, actions, attendance } = clock;

  const status =
    phase === 'working' || phase === 'break' ? (
      <ClockStatusBadge phase={phase} isOnLunchBreak={clock.isOnLunchBreak} />
    ) : (
      <Badge variant="outline" className="whitespace-nowrap">
        {phase === 'done'
          ? t(attendance?.IsForceClockOut ? 'DAY_CLOSED_AUTOMATICALLY' : 'DAY_COMPLETED')
          : t('NOT_CLOCKED_IN')}
      </Badge>
    );

  return (
    <>
      {/* The day's status chip sits right beside the greeting (2 Nov 2026); View stays top right. */}
      <HeroHeader
        badge={clock.isClockStateLoading ? <Skeleton className="h-5 w-24" /> : status}
        aside={
          <Link
            to={APP_PATHS.ATTENDANCE}
            className="shrink-0 text-sm font-medium text-primary hover:underline"
          >
            {t('VIEW')}
          </Link>
        }
      />
      <div className="grid items-center gap-x-8 gap-y-3 border-t border-[color-mix(in_srgb,oklch(var(--primary))_16%,oklch(var(--border)))] pt-4 md:grid-cols-[auto_1fr]">
        {/* Left: "Total worked: <time>" on one line, centred against the right-hand block. */}
        <div className="flex flex-wrap items-center gap-x-2 self-center">
          <span className="text-sm text-muted-foreground">
            {phase === 'done' ? t('TOTAL_WORKED') : t('WORKED_TODAY')}:
          </span>
          {clock.isLoading ? (
            <Skeleton className="h-9 w-40 self-center" />
          ) : (
            <b className="text-4xl font-semibold leading-none tracking-[-0.02em] tabular-nums">
              {formatElapsed(clock.workedSeconds)}
            </b>
          )}
        </div>

        {/* Right: the day's times, then the actions and the first rule message (agreed 2 Nov 2026). */}
        <div className="grid gap-3 md:justify-items-end">
          <div className="flex flex-wrap gap-x-6 gap-y-2 md:justify-end md:text-right">
            <Fact
              label={t('CLOCK_IN')}
              value={timeOf(attendance?.StartTimeUtc, attendance?.Timezone)}
            />
            {clock.canUseBreaks && (
              <Fact
                label={t('HOME_BREAK')}
                value={clock.breakSeconds > 0 ? formatElapsed(clock.breakSeconds) : '--:--:--'}
              />
            )}
            <Fact
              label={t('CLOCK_OUT')}
              value={timeOf(attendance?.EndTimeUtc, attendance?.Timezone)}
            />
          </div>

          <div className="grid gap-1.5 md:justify-items-end">
            {clock.isClockStateLoading ? (
              <Skeleton className="h-9 w-48" />
            ) : (
              <div className="flex flex-wrap gap-2 max-md:[&>*]:flex-[1_1_calc(50%-4px)] md:justify-end">
                {(show.clockIn || show.clockInAgain) && (
                  <Button
                    onClick={actions.clockIn}
                    loading={busy.clockingIn}
                    disabled={busy.clockingIn}
                  >
                    {show.clockIn ? <AlarmClock /> : <LogIn />}
                    {t(show.clockIn ? 'CLOCK_IN' : 'CLOCK_IN_AGAIN')}
                  </Button>
                )}
                {show.endBreak && (
                  <Button
                    variant="outline"
                    onClick={actions.endBreak}
                    loading={busy.endingBreak}
                    disabled={busy.savingBreak || Boolean(clock.endBreakBlocker)}
                  >
                    <Coffee />
                    {t('END_BREAK')}
                  </Button>
                )}
                {show.takeBreak && (
                  <Button
                    variant="outline"
                    onClick={() => actions.startBreak('Regular')}
                    loading={busy.startingBreakType === 'Regular'}
                    disabled={busy.savingBreak}
                  >
                    <Coffee />
                    {t('TAKE_BREAK')}
                  </Button>
                )}
                {show.takeLunch && (
                  <Button
                    variant="outline"
                    onClick={() => actions.startBreak('Lunch')}
                    loading={busy.startingBreakType === 'Lunch'}
                    disabled={busy.savingBreak}
                  >
                    <UtensilsCrossed />
                    {t('TAKE_LUNCH_BREAK')}
                  </Button>
                )}
                {show.clockOut && (
                  <Button
                    variant="destructive"
                    onClick={actions.clockOut}
                    disabled={busy.clockingOut}
                  >
                    <LogOut />
                    {t('CLOCK_OUT')}
                  </Button>
                )}
              </div>
            )}
            {!clock.isClockStateLoading && clock.hints[0] && (
              <p className="max-w-[380px] text-[13px] text-muted-foreground md:text-right">
                {clock.hints[0]}
              </p>
            )}
          </div>
        </div>
        {clock.dialogs}
      </div>
    </>
  );
};

/** The band's first row: the greeting with `badge` beside it, the date under them, and `aside` at the
 * top right. */
const HeroHeader = ({ badge, aside }: { badge?: ReactNode; aside?: ReactNode }) => {
  const { t } = useTranslation();
  const firstName = useAuthStore((state) => state.user?.firstName);
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-2xl font-semibold leading-8 tracking-[-0.01em]">
            {t(greetingKey(), { name: firstName ?? '' })}
          </h1>
          {badge}
        </div>
        <p className="mt-0.5 text-muted-foreground">
          {new Date().toLocaleDateString(undefined, {
            timeZone: getOrganizationTimeZone(),
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
      </div>
      {aside && <div className="flex shrink-0 items-center gap-3 pt-1">{aside}</div>}
    </div>
  );
};

/**
 * Home's hero band (HR Module Prototype › Home, `homeHero`; agreed 2 Nov 2026): a primary-tinted band
 * with the greeting, the date and a View link to Attendance, then, for those who use Attendance, the
 * timer row from the same `useAttendanceClock` as the Attendance card.
 */
export const HomeHero = ({ canUseAttendance }: { canUseAttendance: boolean }) => (
  <section className="grid gap-4 rounded-lg border border-[color-mix(in_srgb,oklch(var(--primary))_22%,oklch(var(--border)))] bg-[color-mix(in_srgb,oklch(var(--primary))_6%,oklch(var(--card)))] px-6 py-5 max-md:p-4">
    {/* The timer's hook runs only for those who use Attendance (its queries would fail otherwise). */}
    {canUseAttendance ? <HeroClock /> : <HeroHeader />}
  </section>
);
